import { useCallback, useEffect, useRef, useState } from "react";

import {
    isSuccessResponse,
    readLastMethod,
    storeLastMethod,
    verificationRequest,
} from "@helpers/verification";

export const STEPS = {
    METHOD: "method",
    CODE: "code",
    LOCKED: "locked",
};

export const CHANNELS = {
    EMAIL: "email",
    SMS: "sms",
};

// Scope → i18n key of the action label used in the subtitle ("Para {{action}}…").
const SCOPE_ACTION_KEYS = {
    "wallet.withdrawal_account": "walletWithdrawalAccount",
    "user.phone.enroll": "userPhoneEnroll",
    "user.phone": "userPhone",
};

export function scopeActionKey(scope) {
    return SCOPE_ACTION_KEYS[scope] || "default";
}

// How long the success choreography holds the modal before the grant resolves
// and the host closes it (contract: ~700 ms after the animation starts).
const SUCCESS_HOLD_MS = 700;
// Wrong code: let the shake finish before the squares are cleared.
const ERROR_CLEAR_MS = 420;

function statusOf(response) {
    return response?.status ?? response?.response?.status ?? null;
}

function dataOf(response) {
    return response?.data ?? response?.response?.data ?? null;
}

/**
 * One verification flow — the state behind a single entry of the host's stack.
 * The hook lives in the per-flow controller (outside the Modal), so its state
 * survives while a nested flow sits on top and the modal below is closed.
 *
 * Steps: method → (request) → code → done | locked. The method step is skipped
 * when exactly one channel is eligible or the last used channel still is.
 */
export default function useVerificationFlow({ flow, onFinish }) {
    const verification = flow.verification || {};
    const scope = verification.scope;

    const [methods, setMethods] = useState(() =>
        (verification.methods || []).filter((m) => m?.channel),
    );
    const [unverified, setUnverified] = useState(() =>
        (verification.unverified || []).filter((m) => m?.channel),
    );
    const [step, setStep] = useState(() =>
        verification.reason === "locked" ? STEPS.LOCKED : STEPS.METHOD,
    );
    const [channel, setChannel] = useState(null);
    const [target, setTarget] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [resendAt, setResendAt] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const [code, setCode] = useState("");
    const [codeStatus, setCodeStatus] = useState("idle");
    const [message, setMessage] = useState(() =>
        verification.grant_rejected ? { kind: "grantRejected", tone: "info" } : null,
    );
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyingPhone, setVerifyingPhone] = useState(false);
    const [lockedUntil, setLockedUntil] = useState(verification.locked_until || null);

    const codeInputRef = useRef(null);
    const finished = useRef(false);
    const started = useRef(false);
    // Whether a code was sent in this flow: an auto-request opens the code
    // step before the answer comes back, and a failed send must not leave the
    // person on a code step that never got a code.
    const codeSent = useRef(false);
    const timers = useRef([]);
    // Every request this flow makes carries this signal. Finishing the flow
    // aborts it, so a request still in flight when the person closes the modal
    // cannot come back as a 428 and open a flow nobody asked for.
    const aborter = useRef(null);
    if (!aborter.current) aborter.current = new AbortController();

    const later = useCallback((fn, ms) => {
        const id = setTimeout(fn, ms);
        timers.current.push(id);
        return id;
    }, []);

    useEffect(() => {
        const pending = timers.current;
        const controller = aborter.current;
        return () => {
            pending.forEach(clearTimeout);
            // Host unmount: the host already released the caller; make sure
            // nothing of ours lands after the fact.
            finished.current = true;
            controller.abort();
        };
    }, []);

    // One-second tick for the resend / expiry countdowns. A flow is short-lived,
    // so one interval for its lifetime is cheaper than starting and stopping it.
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const finish = useCallback(
        (result) => {
            if (finished.current) return;
            finished.current = true;
            aborter.current.abort();
            onFinish(flow, result);
        },
        [flow, onFinish],
    );

    const lock = useCallback((until) => {
        setLockedUntil(until || null);
        setCode("");
        setCodeStatus("idle");
        setMessage(null);
        setStep(STEPS.LOCKED);
    }, []);

    // ── request-code ────────────────────────────────────────────────────
    const requestCode = useCallback(
        async (nextChannel) => {
            setSending(true);
            // Errors are stale once a new code is on its way; an info note (the
            // previous grant expired) is the reason the person is here at all,
            // and the auto-request would otherwise wipe it before it is seen.
            // It clears on the first keystroke.
            setMessage((current) => (current?.tone === "info" ? current : null));
            setChannel(nextChannel);

            // Not skip-flagged on purpose: for `user.phone.enroll` core answers
            // 428 `required` (scope user.phone) and the interceptor nests the
            // e-mail flow on top of this one.
            const response = await verificationRequest(
                "post",
                "auth/verification/request",
                { scope, channel: nextChannel },
                { _verificationRequest: true, signal: aborter.current.signal },
            );

            // The flow ended (closed, or the host went away) while the request
            // was in flight: nothing below applies any more.
            if (finished.current) return false;

            setSending(false);

            const status = statusOf(response);
            const data = dataOf(response) || {};

            if (isSuccessResponse(response)) {
                codeSent.current = true;
                setTarget(
                    data.target || methods.find((m) => m.channel === nextChannel)?.target || null,
                );
                setExpiresAt(Date.now() + (Number(data.expires_in) || 300) * 1000);
                setResendAt(Date.now() + (Number(data.resend_in) || 60) * 1000);
                setCode("");
                setCodeStatus("idle");
                setStep(STEPS.CODE);
                return true;
            }

            // The nested flow was dismissed: this one has no way forward either.
            if (response?.verificationCancelled) {
                finish({ grant: null, outcome: "cancelled" });
                return false;
            }

            // The lock was met — and acknowledged — inside the nested flow; the
            // person has seen it once, so hand the outcome up instead of
            // showing the same screen again.
            if (response?.verificationLocked) {
                finish({ grant: null, outcome: "locked" });
                return false;
            }

            if (status === 428 && data.verification?.reason === "locked") {
                lock(data.verification?.locked_until);
                return false;
            }

            if (status === 429) {
                const retryAfter = Number(data.retry_after) || 60;
                setResendAt(Date.now() + retryAfter * 1000);
                setMessage({ kind: "rateLimited", tone: "error" });
                // Throttled before anything was sent: the pre-opened code step
                // would claim a code is on its way.
                if (!codeSent.current) setStep(STEPS.METHOD);
                return false;
            }

            if (status === 422 && data.status === "channel_not_available") {
                setStep(STEPS.METHOD);
                setMessage({ kind: "channelNotAvailable", tone: "error" });
                return false;
            }

            setMessage({ kind: "sendFailed", tone: "error" });
            // Nothing was ever sent: a code step would have nothing to type.
            if (!codeSent.current) setStep(STEPS.METHOD);
            return false;
        },
        [scope, methods, finish, lock],
    );

    // ── verify ──────────────────────────────────────────────────────────
    const verifyCode = useCallback(
        async (value) => {
            if (verifying || codeStatus === "success" || finished.current) return;

            setVerifying(true);
            setMessage(null);

            const response = await verificationRequest(
                "post",
                "auth/verification/verify",
                { scope, code: value },
                { _skipVerification: true },
            );

            setVerifying(false);

            const status = statusOf(response);
            const data = dataOf(response) || {};

            if (isSuccessResponse(response) && data.grant) {
                storeLastMethod(channel);
                setCodeStatus("success");
                later(() => finish({ grant: data.grant, outcome: "verified" }), SUCCESS_HOLD_MS);
                return;
            }

            const reason = status === 428 ? data.verification?.reason : null;

            if (reason === "locked") {
                lock(data.verification?.locked_until);
                return;
            }

            const clearAfterShake = () =>
                later(() => {
                    setCode("");
                    setCodeStatus("idle");
                    codeInputRef.current?.focus(0);
                }, ERROR_CLEAR_MS);

            if (reason === "invalid") {
                setCodeStatus("error");
                setMessage({
                    kind: "invalid",
                    tone: "error",
                    attemptsLeft: data.verification?.attempts_left,
                });
                clearAfterShake();
                return;
            }

            if (reason === "expired") {
                setCodeStatus("error");
                setMessage({ kind: "expired", tone: "error" });
                setExpiresAt(null);
                setResendAt(null);
                clearAfterShake();
                return;
            }

            setCodeStatus("error");
            setMessage({ kind: "verifyFailed", tone: "error" });
            clearAfterShake();
        },
        [verifying, codeStatus, scope, channel, finish, lock, later],
    );

    // ── phone enrollment (from the method step) ─────────────────────────
    const verifyPhone = useCallback(async () => {
        setVerifyingPhone(true);
        setMessage(null);

        // The interceptor nests the `user.phone.enroll` flow (and, under it,
        // the `user.phone` e-mail flow) and replays this call with the grant.
        const response = await verificationRequest("post", "user/phone/confirm", undefined, {
            signal: aborter.current.signal,
        });

        if (finished.current) return;

        setVerifyingPhone(false);

        if (isSuccessResponse(response)) {
            const sms = unverified.find((u) => u.channel === CHANNELS.SMS);
            setUnverified((prev) => prev.filter((u) => u.channel !== CHANNELS.SMS));
            setMethods((prev) =>
                prev.some((m) => m.channel === CHANNELS.SMS)
                    ? prev
                    : [...prev, { channel: CHANNELS.SMS, target: sms?.target || null }],
            );
            await requestCode(CHANNELS.SMS);
            return;
        }

        // Backed out of the nested flow: stay on the method step, nothing to say.
        if (response?.verificationCancelled) return;

        // Locked — already shown and acknowledged in the flow the interceptor
        // opened for this call (or nested under it). Once is enough.
        if (response?.verificationLocked) {
            finish({ grant: null, outcome: "locked" });
            return;
        }

        setMessage({ kind: "phoneVerifyFailed", tone: "error" });
    }, [unverified, requestCode, finish]);

    // ── start: skip the method step when the choice is already made ─────
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        if (step === STEPS.LOCKED) return;

        // The choice is already made: open straight on the code step (target
        // from the 428 payload) while the code is on its way, instead of
        // flashing the chooser with a spinner. requestCode falls back to the
        // method step when the channel turns out unavailable or the send fails.
        const startWith = (nextChannel) => {
            setChannel(nextChannel);
            setTarget(methods.find((m) => m.channel === nextChannel)?.target || null);
            setStep(STEPS.CODE);
            requestCode(nextChannel);
        };

        if (methods.length === 1) {
            startWith(methods[0].channel);
            return;
        }

        const last = readLastMethod();
        if (last && methods.some((m) => m.channel === last)) {
            startWith(last);
        }
        // mount only — the flow's inputs never change after it is created
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The code outlived its 5 minutes while the person was looking at it.
    useEffect(() => {
        if (step !== STEPS.CODE || !expiresAt || now < expiresAt) return;
        if (codeStatus === "success") return;
        setExpiresAt(null);
        setResendAt(null);
        setMessage({ kind: "expired", tone: "error" });
    }, [now, expiresAt, step, codeStatus]);

    // A rate-limit message is only true while its countdown runs.
    useEffect(() => {
        if (message?.kind !== "rateLimited") return;
        if (resendAt && now < resendAt) return;
        setMessage(null);
    }, [now, resendAt, message]);

    // ── actions exposed to the view ─────────────────────────────────────
    const cancel = useCallback(() => {
        if (codeStatus === "success") return; // non-dismissable during the success beat
        // Esc / X / backdrop on the locked step still means "locked": the
        // caller must not mistake it for the person changing their mind.
        finish({ grant: null, outcome: step === STEPS.LOCKED ? "locked" : "cancelled" });
    }, [codeStatus, step, finish]);

    const acknowledgeLock = useCallback(() => {
        finish({ grant: null, outcome: "locked" });
    }, [finish]);

    const chooseAnother = useCallback(() => {
        setStep(STEPS.METHOD);
        setCode("");
        setCodeStatus("idle");
        setMessage(null);
    }, []);

    const resend = useCallback(() => {
        if (channel) return requestCode(channel);
        return Promise.resolve(false);
    }, [channel, requestCode]);

    const handleCodeChange = useCallback((value) => {
        setCode(value);
        // The grant-expired note has done its job once the person starts
        // typing; errors stay until the next verify or resend clears them.
        setMessage((current) => (current?.tone === "info" ? null : current));
    }, []);

    const handleCodeComplete = useCallback(
        (value) => {
            verifyCode(value);
        },
        [verifyCode],
    );

    const resendIn = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;
    const expiresIn = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : null;
    const canResend =
        step === STEPS.CODE && !sending && !verifying && resendIn === 0 && codeStatus !== "success";

    return {
        scope,
        actionKey: scopeActionKey(scope),
        step,
        methods,
        unverified,
        channel,
        target,
        code,
        codeStatus,
        message,
        sending,
        verifying,
        verifyingPhone,
        lockedUntil,
        resendIn,
        expiresIn,
        canResend,
        codeInputRef,
        requestCode,
        verifyPhone,
        resend,
        chooseAnother,
        cancel,
        acknowledgeLock,
        handleCodeChange,
        handleCodeComplete,
    };
}
